import { IpBlockModel } from '@/http/models/IpBlock'
import { toast } from 'sonner'

import { useState } from 'react'

import { IpBlockModel as IpBlockType } from '@/types'

import { useFetchIpBlocks } from './useFetchIpBlocks'

type DialogMode = 'create' | 'edit' | 'view'

export const useIpBlocksTable = () => {
  const [mode, setMode] = useState<DialogMode>('create')
  const [ipBlockDialogOpen, setIpBlockDialogOpen] = useState(false)
  const [editIpBlock, setEditIpBlock] = useState<IpBlockType.IpBlock | null>(null)

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [ipBlockToDelete, setIpBlockToDelete] = useState<IpBlockType.IpBlock | null>(null)

  const [createLoading, setCreateLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const { loading, data, pagination, formValues, formMethods, setPage, setPageSize, refresh } =
    useFetchIpBlocks({
      sort: null,
    })

  const openCreateDialog = () => {
    setMode('create')
    setEditIpBlock(null)
    setIpBlockDialogOpen(true)
  }

  const openViewDialog = (ipBlock: IpBlockType.IpBlock) => {
    setMode('view')
    setEditIpBlock(ipBlock)
    setIpBlockDialogOpen(true)
  }

  const openEditDialog = (ipBlock: IpBlockType.IpBlock) => {
    setMode('edit')
    setEditIpBlock(ipBlock)
    setIpBlockDialogOpen(true)
  }

  const closeDialog = () => {
    setIpBlockDialogOpen(false)
    setEditIpBlock(null)
  }

  const handleDeleteClick = (ipBlock: IpBlockType.IpBlock) => {
    setIpBlockToDelete(ipBlock)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!ipBlockToDelete?._id) return

    setDeleteLoading(true)
    const response = await IpBlockModel.delete({
      params: { id: ipBlockToDelete._id },
    })

    if (response.success) {
      toast.success('IP Address unblocked successfully')
      setDeleteDialogOpen(false)
      setIpBlockToDelete(null)
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to unblock IP')
    }
    setDeleteLoading(false)
  }

  const toggleStatus = async (ipBlock: IpBlockType.IpBlock) => {
    const newStatus = ipBlock.status === 'active' ? 'inactive' : 'active'

    const response = await IpBlockModel.update({
      params: { id: ipBlock._id },
      body: { status: newStatus },
    })

    if (response.success) {
      toast.success('IP status updated')
      refresh()
    } else {
      toast.error('Failed to update status')
    }
  }

  const handleCreateIpBlock = async (data: any) => {
    setCreateLoading(true)
    const response = await IpBlockModel.create({
      body: data,
    })

    if (response.success) {
      toast.success('IP Address blocked successfully')
      closeDialog()
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to block IP')
    }
    setCreateLoading(false)
  }

  const handleUpdateIpBlock = async (id: string, data: any) => {
    setUpdateLoading(true)
    const response = await IpBlockModel.update({
      params: { id },
      body: data,
    })

    if (response.success) {
      toast.success('IP Block updated successfully')
      closeDialog()
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to update IP block')
    }
    setUpdateLoading(false)
  }

  const stats = {
    totalIpBlocks: pagination.totalCount,
    activeIpBlocks: data.filter((ip) => ip.status === 'active').length,
    inactiveIpBlocks: data.filter((ip) => ip.status === 'inactive').length,
  }

  return {
    loading,
    data,
    pagination,
    formValues,
    formMethods,
    stats,
    mode,
    ipBlockDialogOpen,
    editIpBlock,
    deleteDialogOpen,
    ipBlockToDelete,
    createLoading,
    updateLoading,
    deleteLoading,
    setPage,
    setPageSize,
    refresh,
    openCreateDialog,
    openViewDialog,
    openEditDialog,
    closeDialog,
    handleDeleteClick,
    handleDeleteConfirm,
    setDeleteDialogOpen,
    handleCreateIpBlock,
    handleUpdateIpBlock,
    toggleStatus,
  }
}
